/**
 * features/hr/CollectionCheckModal.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — the check that opens whenever someone emails payslips.
 *
 * Side by side: every collection in the run down the left (PAYE, school
 * loans, salary advances, medical aid, …), each with its total, last
 * month's, and a tick; the chosen one's staff breakdown open on the
 * right, changes first. Emailing stays disabled until every collection
 * is ticked. The logic, and why it compares with LAST MONTH rather than
 * the portal's own schedule, is in lib/collectionCheck.ts.
 *
 * A tick is saved (hrPayrollCollectionChecks/{YYYY-MM}) with the total,
 * who ticked it and when, and audited. It holds only while that total
 * stands: regenerate a payslip so a total moves, and that collection
 * asks to be checked again. A second batch later in the month, with
 * nothing changed, opens already ticked.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { X, Mail, AlertTriangle, ListChecks } from 'lucide-react';
import { modalBackdrop, modalSurface, btnPrimary, btnGhost } from '@/features/finance/ui';
import { fmtPula } from '@/lib/hrPayroll';
import {
  changeSummary, rowsByConcern, tickHolds, tickLapsed, allTicked,
  type Collection, type CollectionRow, type CollectionTick,
} from '@/lib/collectionCheck';

const BAR: Record<Collection['state'], string> = {
  ok: 'border-l-emerald-500',
  warn: 'border-l-amber-500',
  bad: 'border-l-red-500',
};

function signed(n: number): string {
  if (Math.abs(n) < 0.005) return 'P0.00';
  return `${n > 0 ? '+' : '−'}${fmtPula(Math.abs(n))}`;
}

function DeltaPill({ c }: { c: Collection }) {
  if (c.state === 'ok' && Math.abs(c.change) < 0.005) {
    return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 whitespace-nowrap">✓ Same as last month</span>;
  }
  const bad = c.state === 'bad';
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap tabular-nums ${bad
      ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}
    >
      {bad ? '⚠ ' : ''}{signed(c.change)}
    </span>
  );
}

function RowFlag({ r, c }: { r: CollectionRow; c: Collection }) {
  const base = 'text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap';
  switch (r.kind) {
    case 'owing': return <span className={`${base} bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`}>Stopped · {fmtPula(r.lastBalance ?? 0)} still owing</span>;
    case 'stopped': return <span className={`${base} bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300`}>{c.carriesBalance ? 'Paid off' : 'Stopped'}</span>;
    case 'added': return <span className={`${base} bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300`}>New this month</span>;
    case 'changed': return <span className={`${base} bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300`}>Amount changed</span>;
    default: return null;
  }
}

export interface CollectionCheckModalProps {
  open: boolean;
  onClose: () => void;
  periodLabel: string;
  collections: Collection[];
  ticks: Record<string, CollectionTick | undefined>;
  onTick: (c: Collection, on: boolean) => Promise<void>;
  /** Payslips that will be emailed. */
  sendCount: number;
  /** Selected staff with no email address — skipped, not blocking. */
  noEmail: string[];
  /** Selected staff whose payslip is not generated yet — not sent. */
  notGenerated: string[];
  onSend: () => void;
}

export default function CollectionCheckModal({
  open, onClose, periodLabel, collections, ticks, onTick, sendCount, noEmail, notGenerated, onSend,
}: CollectionCheckModalProps) {
  const [sel, setSel] = useState<string>('');
  const [changesOnly, setChangesOnly] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState('');

  // Open on the collection most in need of a look.
  useEffect(() => {
    if (!open) return;
    setError('');
    const first = collections.find((c) => c.state === 'bad') ?? collections.find((c) => c.state === 'warn') ?? collections[0];
    setSel(first?.key ?? '');
    setChangesOnly(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const cur = collections.find((c) => c.key === sel) ?? collections[0];
  const done = collections.filter((c) => tickHolds(ticks[c.key], c)).length;
  const ready = collections.length === 0 || allTicked(collections, ticks);
  const shown = useMemo(() => {
    if (!cur) return [];
    const rows = rowsByConcern(cur.rows);
    return changesOnly ? rows.filter((r) => r.kind !== 'same') : rows;
  }, [cur, changesOnly]);

  async function toggle(c: Collection, on: boolean) {
    setBusyKey(c.key);
    setError('');
    try { await onTick(c, on); } catch (e) { setError((e as Error).message); } finally { setBusyKey(null); }
  }

  if (!open) return null;

  const tickBox = (c: Collection, big = false) => {
    const held = tickHolds(ticks[c.key], c);
    return big ? (
      <label className="flex items-center gap-3 rounded-xl border-2 border-[var(--accent-strong)] bg-violet-50 dark:bg-violet-950/30 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white cursor-pointer">
        <input
          type="checkbox" className="w-[18px] h-[18px] accent-[var(--accent-strong)]"
          checked={held} disabled={busyKey === c.key}
          onChange={(e) => toggle(c, e.target.checked)}
        />
        I have checked this total of {fmtPula(c.now)} against what we expected to collect
      </label>
    ) : (
      <label
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox" className="accent-[var(--accent-strong)]"
          checked={held} disabled={busyKey === c.key}
          onChange={(e) => toggle(c, e.target.checked)}
          aria-label={`I have checked ${c.label}`}
        />
        Checked
      </label>
    );
  };

  return (
    <div className={modalBackdrop} onClick={onClose}>
      <div
        className={modalSurface('hr-scope w-full max-w-6xl max-h-[92vh] flex flex-col')}
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-labelledby="cc-title"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div className="min-w-0">
            <h2 id="cc-title" className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ListChecks size={18} className="text-[var(--accent-strong)]" /> Before you email {periodLabel} payslips
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Check each collection total against what you expected to collect. Pick one on the left to see every staff member behind it.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-3 min-h-0">
          {collections.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">This run has no deductions to check.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-[minmax(230px,300px)_minmax(0,1fr)] items-start">
              {/* LEFT — every collection */}
              <div className="space-y-2">
                {collections.map((c) => {
                  const held = tickHolds(ticks[c.key], c);
                  const lapsed = tickLapsed(ticks[c.key], c);
                  const active = c.key === cur?.key;
                  return (
                    <div
                      key={c.key}
                      role="button" tabIndex={0}
                      onClick={() => { setSel(c.key); setChangesOnly(true); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSel(c.key); } }}
                      aria-current={active}
                      className={`text-left rounded-r-xl border border-gray-100 dark:border-gray-700/70 border-l-4 ${BAR[c.state]} px-3 py-2.5 shadow-sm cursor-pointer transition-colors ${active
                        ? 'ring-2 ring-[var(--accent-strong)] bg-violet-50 dark:bg-violet-950/30'
                        : held ? 'bg-gray-50 dark:bg-gray-800/60' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/80'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm text-gray-900 dark:text-white truncate">{c.label}</span>
                        {tickBox(c)}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1 flex-wrap">
                        <span className="text-base font-extrabold tabular-nums text-gray-900 dark:text-white">{fmtPula(c.now)}</span>
                        <DeltaPill c={c} />
                      </div>
                      {lapsed && <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 mt-1">Changed after it was checked — check again</p>}
                      {held && <p className="text-[11px] text-gray-400 mt-1">Checked by {ticks[c.key]!.byName}, {new Date(ticks[c.key]!.at).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>}
                    </div>
                  );
                })}
              </div>

              {/* RIGHT — the chosen collection, person by person */}
              {cur && (
                <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3 min-w-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="font-bold text-gray-900 dark:text-white">{cur.label}</h3>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{changeSummary(cur)}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                    <span>This run <b className="tabular-nums text-gray-900 dark:text-white">{fmtPula(cur.now)}</b></span>
                    <span>Last run <b className="tabular-nums text-gray-900 dark:text-white">{fmtPula(cur.last)}</b></span>
                    <span>Change <b className="tabular-nums text-gray-900 dark:text-white">{signed(cur.change)}</b></span>
                    <span>{cur.staffCount} staff</span>
                  </div>
                  {cur.counts.owing > 0 && (
                    <div className="rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 flex gap-2">
                      <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                      <span>
                        <b>{cur.counts.owing} {cur.counts.owing === 1 ? 'deduction' : 'deductions'} stopped while money is still owed.</b>{' '}
                        Usually a start month or instalment set wrong. Fix it under Loans, regenerate the payslip, then check this total again.
                      </span>
                    </div>
                  )}
                  <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden text-sm font-semibold" role="group" aria-label="Show">
                    {([['Changes only', true], ['Everyone', false]] as const).map(([label, val]) => (
                      <button
                        key={label}
                        onClick={() => setChangesOnly(val)}
                        aria-pressed={changesOnly === val}
                        className={`px-3 py-1.5 ${changesOnly === val ? 'bg-[var(--accent-strong)] text-white' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200'}`}
                      >
                        {label} ({val ? cur.rows.filter((r) => r.kind !== 'same').length : cur.rows.length})
                      </button>
                    ))}
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                          <th className="py-2 px-3 font-semibold">Staff</th>
                          <th className="py-2 px-3 font-semibold text-right">Last month</th>
                          <th className="py-2 px-3 font-semibold text-right">This month</th>
                          <th className="py-2 px-3 font-semibold text-right">Change</th>
                          {cur.carriesBalance && <th className="py-2 px-3 font-semibold text-right">Balance left</th>}
                          <th className="py-2 px-3" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                        {shown.map((r) => (
                          <tr
                            key={r.staffId}
                            className={r.kind === 'owing' ? 'shadow-[inset_3px_0_0_#dc2626]' : r.kind !== 'same' ? 'shadow-[inset_3px_0_0_#d97706]' : ''}
                          >
                            <td className="py-1.5 px-3 text-gray-800 dark:text-gray-100">
                              {r.name}
                              {r.detail && <span className="block text-[11px] text-gray-400">{r.detail}</span>}
                            </td>
                            <td className="py-1.5 px-3 text-right tabular-nums">{r.last ? fmtPula(r.last) : '—'}</td>
                            <td className="py-1.5 px-3 text-right tabular-nums font-semibold">{r.now ? fmtPula(r.now) : '—'}</td>
                            <td className="py-1.5 px-3 text-right tabular-nums">{Math.abs(r.change) >= 0.005 ? signed(r.change) : '—'}</td>
                            {cur.carriesBalance && (
                              <td className="py-1.5 px-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                                {r.balanceLeft !== undefined ? fmtPula(r.balanceLeft) : r.lastBalance !== undefined ? fmtPula(r.lastBalance) : '—'}
                              </td>
                            )}
                            <td className="py-1.5 px-3"><RowFlag r={r} c={cur} /></td>
                          </tr>
                        ))}
                        {shown.length === 0 && (
                          <tr><td colSpan={6} className="py-4 px-3 text-sm text-gray-400">No changes from last month.</td></tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-200 dark:border-gray-600 font-bold text-gray-900 dark:text-white">
                          <td className="py-2 px-3">Total</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmtPula(cur.last)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmtPula(cur.now)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{signed(cur.change)}</td>
                          {cur.carriesBalance && <td />}
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {tickBox(cur, true)}
                </div>
              )}
            </div>
          )}

          {(noEmail.length > 0 || notGenerated.length > 0) && (
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-xs text-gray-600 dark:text-gray-300 space-y-1">
              {noEmail.length > 0 && <p><b>{noEmail.length}</b> selected {noEmail.length === 1 ? 'person has' : 'people have'} no email on file and will be skipped: {noEmail.join(', ')}.</p>}
              {notGenerated.length > 0 && <p><b>{notGenerated.length}</b> selected payslip{notGenerated.length === 1 ? ' is' : 's are'} not generated yet and won’t be sent: {notGenerated.join(', ')}.</p>}
            </div>
          )}
          <p className="text-[11px] text-gray-400">Totals cover the whole run, not just the people ticked for this email.</p>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 dark:border-gray-700">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            <b className="tabular-nums text-gray-900 dark:text-white">{done} of {collections.length}</b> collections checked
          </span>
          <div className="flex gap-2">
            <button className={btnGhost} onClick={onClose}>Cancel</button>
            <button
              className={btnPrimary}
              disabled={!ready || sendCount === 0}
              title={!ready ? 'Tick every collection first' : sendCount === 0 ? 'Nothing to send' : ''}
              onClick={onSend}
            >
              <Mail size={15} /> Email {sendCount} payslip{sendCount === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
